import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import { toast } from "sonner";

interface Tour {
  id: string;
  title: string;
  slug: string;
  destination_id: string | null;
  main_image_url: string | null;
  gallery: string[];
  short_description: string;
  long_description: string;
  duration: string;
  max_capacity: number;
  includes: string[];
  not_includes: string[];
  restrictions: string[];
  available_schedules: string[];
  features: string[];
  highlights: string[];
  what_to_bring: string[];
  what_to_expect: string;
  pricing_mode: string;
  adult_price: number;
  child_price: number;
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
}

interface TourPackage {
  id?: string;
  name: string;
  description: string;
  price: number;
  max_persons: number | null;
  sort_order: number;
}

interface Destination {
  id: string;
  title: string;
}

function slugify(t: string) {
  return t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

const emptyForm = {
  title: "", slug: "", destination_id: "", main_image_url: "",
  gallery: [] as string[], short_description: "", long_description: "",
  duration: "", max_capacity: 20,
  includes: [] as string[], not_includes: [] as string[], restrictions: [] as string[],
  available_schedules: [] as string[], features: [] as string[], highlights: [] as string[],
  what_to_bring: [] as string[], what_to_expect: "",
  pricing_mode: "fixed", adult_price: 0, child_price: 0,
  is_active: true, is_featured: false, sort_order: 0,
};

// Reusable tag-list input
function TagInput({ items, onAdd, onRemove, placeholder }: { items: string[]; onAdd: (v: string) => void; onRemove: (i: number) => void; placeholder: string }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onAdd(val.trim()); setVal(""); } };
  return (
    <div>
      <div className="flex gap-2 mb-1">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} />
        <Button type="button" size="sm" onClick={add}>+</Button>
      </div>
      <div className="flex flex-wrap gap-1">
        {items.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-0.5 rounded text-xs">
            {f} <X className="h-3 w-3 cursor-pointer" onClick={() => onRemove(i)} />
          </span>
        ))}
      </div>
    </div>
  );
}

export function ToursSection() {
  const [tours, setTours] = useState<Tour[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [packages, setPackages] = useState<TourPackage[]>([]);
  const [newGalleryUrl, setNewGalleryUrl] = useState("");

  const fetchData = async () => {
    const [{ data: t }, { data: d }] = await Promise.all([
      supabase.from("tours").select("*").order("sort_order"),
      supabase.from("destinations").select("id, title").order("title"),
    ]);
    setTours((t as any[]) ?? []);
    setDestinations((d as any[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setPackages([]);
    setDialogOpen(true);
  };

  const openEdit = async (t: Tour) => {
    setEditing(t.id);
    setForm({
      title: t.title, slug: t.slug, destination_id: t.destination_id ?? "",
      main_image_url: t.main_image_url ?? "", gallery: t.gallery ?? [],
      short_description: t.short_description ?? "", long_description: t.long_description ?? "",
      duration: t.duration ?? "", max_capacity: t.max_capacity,
      includes: t.includes ?? [], not_includes: t.not_includes ?? [],
      restrictions: t.restrictions ?? [], available_schedules: t.available_schedules ?? [],
      features: t.features ?? [], highlights: t.highlights ?? [],
      what_to_bring: t.what_to_bring ?? [], what_to_expect: t.what_to_expect ?? "",
      pricing_mode: t.pricing_mode ?? "fixed", adult_price: t.adult_price, child_price: t.child_price,
      is_active: t.is_active, is_featured: t.is_featured, sort_order: t.sort_order,
    });
    const { data: pkgs } = await supabase.from("tour_packages").select("*").eq("tour_id", t.id).order("sort_order");
    setPackages((pkgs as any[]) ?? []);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) { toast.error("Título y slug requeridos"); return; }
    const payload: any = {
      title: form.title, slug: form.slug,
      destination_id: form.destination_id || null,
      main_image_url: form.main_image_url || null,
      gallery: form.gallery, short_description: form.short_description,
      long_description: form.long_description, duration: form.duration,
      max_capacity: form.max_capacity, includes: form.includes,
      not_includes: form.not_includes, restrictions: form.restrictions,
      available_schedules: form.available_schedules, features: form.features,
      highlights: form.highlights, what_to_bring: form.what_to_bring,
      what_to_expect: form.what_to_expect, pricing_mode: form.pricing_mode,
      adult_price: form.adult_price, child_price: form.child_price,
      is_active: form.is_active, is_featured: form.is_featured, sort_order: form.sort_order,
    };

    let tourId = editing;
    if (editing) {
      const { error } = await supabase.from("tours").update(payload).eq("id", editing);
      if (error) { toast.error(error.message); return; }
    } else {
      const { data, error } = await supabase.from("tours").insert(payload).select("id").single();
      if (error) { toast.error(error.message); return; }
      tourId = data.id;
    }

    // Save packages if mode is packages
    if (form.pricing_mode === "packages" && tourId) {
      await supabase.from("tour_packages").delete().eq("tour_id", tourId);
      if (packages.length > 0) {
        const pkgs = packages.map((p, i) => ({
          tour_id: tourId,
          name: p.name,
          description: p.description,
          price: p.price,
          max_persons: p.max_persons,
          sort_order: i,
        }));
        await supabase.from("tour_packages").insert(pkgs as any);
      }
    }

    toast.success(editing ? "Tour actualizado" : "Tour creado");
    setDialogOpen(false);
    fetchData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar este tour?")) return;
    await supabase.from("tours").delete().eq("id", id);
    toast.success("Eliminado");
    fetchData();
  };

  const updateFormList = (key: string, items: string[]) => setForm({ ...form, [key]: items });

  const addPackage = () => setPackages([...packages, { name: "", description: "", price: 0, max_persons: null, sort_order: packages.length }]);
  const updatePackage = (i: number, field: string, value: any) => {
    const p = [...packages];
    (p[i] as any)[field] = value;
    setPackages(p);
  };
  const removePackage = (i: number) => setPackages(packages.filter((_, idx) => idx !== i));

  const destName = (id: string | null) => destinations.find((d) => d.id === id)?.title ?? "—";

  if (loading) return <p>Cargando...</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Tours</h2>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Nuevo Tour</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Título</TableHead>
            <TableHead>Destino</TableHead>
            <TableHead>Duración</TableHead>
            <TableHead>Precio</TableHead>
            <TableHead>Activo</TableHead>
            <TableHead>Destacado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tours.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="font-medium">{t.title}</TableCell>
              <TableCell>{destName(t.destination_id)}</TableCell>
              <TableCell>{t.duration}</TableCell>
              <TableCell>{t.pricing_mode === "fixed" ? `$${t.adult_price}` : "Paquetes"}</TableCell>
              <TableCell>{t.is_active ? "✅" : "❌"}</TableCell>
              <TableCell>{t.is_featured ? <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" /> : "—"}</TableCell>
              <TableCell className="text-right space-x-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(t.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </TableCell>
            </TableRow>
          ))}
          {tours.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Sin tours</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Tour" : "Nuevo Tour"}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="basic">
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="basic">Básico</TabsTrigger>
              <TabsTrigger value="description">Descripción</TabsTrigger>
              <TabsTrigger value="details">Detalles</TabsTrigger>
              <TabsTrigger value="pricing">Precio</TabsTrigger>
              <TabsTrigger value="settings">Config</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Título</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} />
                </div>
                <div>
                  <Label>Slug</Label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Destino</Label>
                <Select value={form.destination_id} onValueChange={(v) => setForm({ ...form, destination_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar destino" /></SelectTrigger>
                  <SelectContent>
                    {destinations.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Imagen Principal (URL)</Label>
                <Input value={form.main_image_url} onChange={(e) => setForm({ ...form, main_image_url: e.target.value })} placeholder="https://..." />
              </div>
              <div>
                <Label>Galería</Label>
                <div className="flex gap-2 mb-2">
                  <Input value={newGalleryUrl} onChange={(e) => setNewGalleryUrl(e.target.value)} placeholder="URL de imagen" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), (() => { if (newGalleryUrl.trim()) { setForm({ ...form, gallery: [...form.gallery, newGalleryUrl.trim()] }); setNewGalleryUrl(""); } })())} />
                  <Button type="button" size="sm" onClick={() => { if (newGalleryUrl.trim()) { setForm({ ...form, gallery: [...form.gallery, newGalleryUrl.trim()] }); setNewGalleryUrl(""); } }}>+</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.gallery.map((url, i) => (
                    <div key={i} className="relative group">
                      <img src={url} className="h-14 w-18 object-cover rounded border" />
                      <button onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, idx) => idx !== i) })} className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full h-4 w-4 flex items-center justify-center text-xs">×</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Duración</Label><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="Ej: 4 horas" /></div>
                <div><Label>Capacidad Máxima</Label><Input type="number" value={form.max_capacity} onChange={(e) => setForm({ ...form, max_capacity: parseInt(e.target.value) || 0 })} /></div>
              </div>
            </TabsContent>

            <TabsContent value="description" className="space-y-4">
              <div><Label>Descripción Corta</Label><Textarea rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></div>
              <div><Label>Descripción Larga</Label><Textarea rows={5} value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} /></div>
              <div><Label>What to Expect</Label><Textarea rows={4} value={form.what_to_expect} onChange={(e) => setForm({ ...form, what_to_expect: e.target.value })} /></div>
            </TabsContent>

            <TabsContent value="details" className="space-y-4">
              <div><Label>Incluye</Label><TagInput items={form.includes} onAdd={(v) => updateFormList("includes", [...form.includes, v])} onRemove={(i) => updateFormList("includes", form.includes.filter((_, idx) => idx !== i))} placeholder="Ej: Transporte" /></div>
              <div><Label>No Incluye</Label><TagInput items={form.not_includes} onAdd={(v) => updateFormList("not_includes", [...form.not_includes, v])} onRemove={(i) => updateFormList("not_includes", form.not_includes.filter((_, idx) => idx !== i))} placeholder="Ej: Propinas" /></div>
              <div><Label>Restricciones</Label><TagInput items={form.restrictions} onAdd={(v) => updateFormList("restrictions", [...form.restrictions, v])} onRemove={(i) => updateFormList("restrictions", form.restrictions.filter((_, idx) => idx !== i))} placeholder="Ej: Mayores de 18" /></div>
              <div><Label>Highlights</Label><TagInput items={form.highlights} onAdd={(v) => updateFormList("highlights", [...form.highlights, v])} onRemove={(i) => updateFormList("highlights", form.highlights.filter((_, idx) => idx !== i))} placeholder="Ej: Vista panorámica" /></div>
              <div><Label>What to Bring</Label><TagInput items={form.what_to_bring} onAdd={(v) => updateFormList("what_to_bring", [...form.what_to_bring, v])} onRemove={(i) => updateFormList("what_to_bring", form.what_to_bring.filter((_, idx) => idx !== i))} placeholder="Ej: Protector solar" /></div>
              <div><Label>Horarios Disponibles</Label><TagInput items={form.available_schedules} onAdd={(v) => updateFormList("available_schedules", [...form.available_schedules, v])} onRemove={(i) => updateFormList("available_schedules", form.available_schedules.filter((_, idx) => idx !== i))} placeholder="Ej: 8:00 AM" /></div>
              <div><Label>Características</Label><TagInput items={form.features} onAdd={(v) => updateFormList("features", [...form.features, v])} onRemove={(i) => updateFormList("features", form.features.filter((_, idx) => idx !== i))} placeholder="Ej: Guía bilingüe" /></div>
            </TabsContent>

            <TabsContent value="pricing" className="space-y-4">
              <div>
                <Label>Modalidad de Precio</Label>
                <Select value={form.pricing_mode} onValueChange={(v) => setForm({ ...form, pricing_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Precio Fijo (Adulto/Niño)</SelectItem>
                    <SelectItem value="packages">Por Paquetes</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.pricing_mode === "fixed" ? (
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Precio Adulto ($)</Label><Input type="number" value={form.adult_price} onChange={(e) => setForm({ ...form, adult_price: parseFloat(e.target.value) || 0 })} /></div>
                  <div><Label>Precio Niño ($)</Label><Input type="number" value={form.child_price} onChange={(e) => setForm({ ...form, child_price: parseFloat(e.target.value) || 0 })} /></div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <Label>Paquetes</Label>
                    <Button size="sm" onClick={addPackage}><Plus className="h-3 w-3 mr-1" /> Agregar Paquete</Button>
                  </div>
                  {packages.map((pkg, i) => (
                    <div key={i} className="border rounded-lg p-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Paquete {i + 1}</span>
                        <Button size="icon" variant="ghost" onClick={() => removePackage(i)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Nombre</Label><Input value={pkg.name} onChange={(e) => updatePackage(i, "name", e.target.value)} placeholder="Ej: Familiar" /></div>
                        <div><Label className="text-xs">Precio ($)</Label><Input type="number" value={pkg.price} onChange={(e) => updatePackage(i, "price", parseFloat(e.target.value) || 0)} /></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Descripción</Label><Input value={pkg.description} onChange={(e) => updatePackage(i, "description", e.target.value)} /></div>
                        <div><Label className="text-xs">Max Personas</Label><Input type="number" value={pkg.max_persons ?? ""} onChange={(e) => updatePackage(i, "max_persons", e.target.value ? parseInt(e.target.value) : null)} /></div>
                      </div>
                    </div>
                  ))}
                  {packages.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sin paquetes — agrega uno</p>}
                </div>
              )}
            </TabsContent>

            <TabsContent value="settings" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Orden</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Activo</Label></div>
                <div className="flex items-center gap-2"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} /><Label>Destacado</Label></div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editing ? "Guardar" : "Crear"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
